/**
 * Plane (self-hosted) integration contracts.
 *
 * Two independent pieces of configuration, deliberately kept apart:
 *
 * - **The connection** — base URL, workspace slug and a bot token — is
 *   installation-wide, stored in `system_settings` under
 *   `PLANE_CONNECTION_SETTING_KEY`. One Plane instance serves every project.
 * - **The link** — which Plane project a Taskin project tracks its work in — is
 *   per project, stored on `projection_projects`. It lives in the database and
 *   not in `.taskin/config.json` on purpose: the config file sits inside the
 *   worktree the agent is editing, so an agent could rewrite its own project
 *   link and reach another team's board. A security boundary cannot live
 *   somewhere the thing it constrains can write.
 *
 * The token never appears in this file's outbound shapes. Only its derived
 * status does, exactly as `projectRepositoryFields` exposes
 * `repoCredentialStatus` and never the PAT. The stored shape — which does hold
 * ciphertext — is server-side only (`apps/server/src/settings/planeConnection.ts`),
 * since no client has any use for it.
 */
import { Schema } from "effect";

import { IsoDateTime, TrimmedNonEmptyString } from "./baseSchemas";

/**
 * The `system_settings.key` holding the Plane connection.
 *
 * A plain string constant, matching `GENERATION_DEFAULTS_SETTING_KEY`: the
 * table is generic and each key is only ever named by the code that owns it.
 */
export const PLANE_CONNECTION_SETTING_KEY = "plane-connection";

/**
 * Where an unconfigured installation points.
 *
 * Defaults rather than required fields, following the same reasoning as the
 * Midas Claude Code plugin: an empty stored value must not produce URLs with an
 * empty workspace slug, which 404 in a way that reads like a missing project
 * rather than missing configuration.
 */
export const DEFAULT_PLANE_BASE_URL = "https://pm.midasred.com";
export const DEFAULT_PLANE_WORKSPACE_SLUG = "departamento-ingenieria";

/**
 * Whether the stored Plane token has been proven to work.
 *
 * Same three states and the same meaning as `ProjectRepoCredentialStatus`, but
 * a separate type rather than a reuse: these are different credentials against
 * different services, and sharing the literal would mean a future Git-only
 * state (a scope distinction, say) silently widening what a Plane status can be.
 *
 * A token that was saved but never exercised is `none` ("sin probar"), not
 * `invalid`: nothing has failed yet.
 */
export const PlaneCredentialStatus = Schema.Literals(["none", "valid", "invalid"]);
export type PlaneCredentialStatus = typeof PlaneCredentialStatus.Type;

/**
 * A Plane project identifier — the human-readable prefix its issues carry
 * (`1GAME`, `TASKIN`), as shown in the Plane UI and used in `1GAME-2`.
 *
 * Upper-case alphanumerics only, which is what Plane itself enforces. Validated
 * here because this string is concatenated into issue references that the
 * confinement check compares against: a slug with a `-` in it would make
 * `1GAME-X-2` ambiguous to parse.
 */
export const PlaneProjectIdentifier = TrimmedNonEmptyString.check(Schema.isPattern(/^[A-Z0-9]+$/));
export type PlaneProjectIdentifier = typeof PlaneProjectIdentifier.Type;

/**
 * A Plane issue reference in its readable form: `<IDENTIFIER>-<sequence>`.
 *
 * The team already writes these in commit messages, so the tools accept and
 * echo the same notation instead of UUIDs. Normalisation and the
 * project-confinement check live server-side (`plane/planeIssueRef.ts`): this
 * package stays schema-only, and the check needs the thread's linked project,
 * which is not part of the value.
 */
export const PlaneIssueRef = TrimmedNonEmptyString.check(
  Schema.isPattern(/^[A-Z0-9]+-[1-9][0-9]*$/),
);
export type PlaneIssueRef = typeof PlaneIssueRef.Type;

const nullableText = Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
  Schema.withDecodingDefault(() => null),
);

/**
 * Plane link state carried on every projected project.
 *
 * Shared verbatim by the full and shell project schemas, mirroring
 * `projectRepositoryFields`, so the settings UI can rehydrate the link without
 * a second round trip.
 *
 * `planeWorkspaceSlug` is `null` when the project uses the installation's
 * workspace. It exists because one Plane instance holds several workspaces and
 * projects legitimately live in different ones — before it, every URL was built
 * with a single global slug and a board outside it was simply unreachable.
 *
 * No credential fields here any more: an agent acts with the Plane account of
 * the human who owns the thread, so "can this project reach Plane?" has a
 * different answer per person and is reported by their own credential status,
 * not by the project.
 */
export const projectPlaneFields = {
  planeProjectId: nullableText,
  planeProjectIdentifier: nullableText,
  planeWorkspaceSlug: nullableText,
} as const;

/**
 * What the admin screen shows for the installation-wide connection.
 *
 * The token is represented only by `tokenStatus` / `tokenValidatedAt` /
 * `tokenLastError`. That triple is what makes an expired token diagnosable
 * without re-entering it, which is the same reason
 * `projectRepositoryFields` carries it for PATs.
 */
export const PlaneConnectionView = Schema.Struct({
  baseUrl: TrimmedNonEmptyString,
  workspaceSlug: TrimmedNonEmptyString,
  tokenStatus: PlaneCredentialStatus,
  tokenValidatedAt: Schema.NullOr(IsoDateTime),
  tokenLastError: Schema.NullOr(TrimmedNonEmptyString),
});
export type PlaneConnectionView = typeof PlaneConnectionView.Type;

/**
 * `PUT /api/admin/plane/connection`.
 *
 * `token` is optional and write-only: omitting it keeps whatever is stored, so
 * an admin can correct the workspace slug without re-pasting the credential.
 * Sending an empty string clears it — an explicit disconnect, distinct from
 * "leave it alone", which is why this is `NullOr` and not just optional.
 */
export const PlaneConnectionUpdateRequest = Schema.Struct({
  baseUrl: TrimmedNonEmptyString,
  workspaceSlug: TrimmedNonEmptyString,
  token: Schema.optional(Schema.NullOr(Schema.String)),
});
export type PlaneConnectionUpdateRequest = typeof PlaneConnectionUpdateRequest.Type;

export const AdminPlaneConnectionResponse = Schema.Struct({
  planeConnection: PlaneConnectionView,
});
export type AdminPlaneConnectionResponse = typeof AdminPlaneConnectionResponse.Type;

/**
 * One entry of the Plane project picker shown when linking a Taskin project.
 *
 * Deliberately three fields: the UUID the link stores, the identifier the tools
 * speak, and the name a human recognises.
 */
export const PlaneProjectSummary = Schema.Struct({
  id: TrimmedNonEmptyString,
  identifier: TrimmedNonEmptyString,
  name: TrimmedNonEmptyString,
});
export type PlaneProjectSummary = typeof PlaneProjectSummary.Type;

export const AdminPlaneProjectsResponse = Schema.Struct({
  projects: Schema.Array(PlaneProjectSummary),
});
export type AdminPlaneProjectsResponse = typeof AdminPlaneProjectsResponse.Type;

/**
 * `PUT /api/admin/plane/projects/:projectId`.
 *
 * `planeProjectId: null` unlinks, which also makes the Plane MCP servers stop
 * being mounted for that project's threads.
 *
 * `workspaceSlug` is the workspace the chosen board lives in; omitted or empty
 * means the installation's. No token: credentials belong to people now, not to
 * projects.
 */
export const PlaneProjectLinkUpdateRequest = Schema.Struct({
  planeProjectId: Schema.NullOr(TrimmedNonEmptyString),
  planeProjectIdentifier: Schema.NullOr(PlaneProjectIdentifier),
  workspaceSlug: Schema.optional(Schema.NullOr(Schema.String)),
});
export type PlaneProjectLinkUpdateRequest = typeof PlaneProjectLinkUpdateRequest.Type;

/**
 * A user's own Plane credential, as they see it.
 *
 * The password and the minted token never appear: what a person needs to know
 * is which account is configured and whether Plane still accepts it. The email
 * is shown because "am I posting as the right account?" is the question this
 * screen answers.
 */
export const UserPlaneCredentialView = Schema.Struct({
  planeEmail: Schema.NullOr(TrimmedNonEmptyString),
  status: PlaneCredentialStatus,
  /** Whether an API token has been minted from the stored password. */
  hasApiToken: Schema.Boolean,
  validatedAt: Schema.NullOr(IsoDateTime),
  lastError: Schema.NullOr(TrimmedNonEmptyString),
  /** Workspaces this account belongs to, reported at save time. */
  workspaceSlugs: Schema.Array(TrimmedNonEmptyString),
});
export type UserPlaneCredentialView = typeof UserPlaneCredentialView.Type;

/**
 * `PUT /api/plane/me/credential` — a person storing their own Plane login.
 *
 * Both fields are required: unlike the installation token, there is no "keep
 * what is stored" case, because the only reason to open this form is to set or
 * replace the credential. Deleting it is `DELETE` on the same route, which also
 * revokes the token in Plane.
 */
export const UserPlaneCredentialUpdateRequest = Schema.Struct({
  planeEmail: TrimmedNonEmptyString,
  password: TrimmedNonEmptyString,
});
export type UserPlaneCredentialUpdateRequest = typeof UserPlaneCredentialUpdateRequest.Type;

export const UserPlaneCredentialResponse = Schema.Struct({
  credential: UserPlaneCredentialView,
});
export type UserPlaneCredentialResponse = typeof UserPlaneCredentialResponse.Type;

/** One row of the admin's overview: who has configured Plane, and how it is doing. */
export const AdminUserPlaneCredentialRow = Schema.Struct({
  userId: TrimmedNonEmptyString,
  planeEmail: Schema.NullOr(TrimmedNonEmptyString),
  status: PlaneCredentialStatus,
  hasApiToken: Schema.Boolean,
  validatedAt: Schema.NullOr(IsoDateTime),
  lastError: Schema.NullOr(TrimmedNonEmptyString),
});
export type AdminUserPlaneCredentialRow = typeof AdminUserPlaneCredentialRow.Type;

export const AdminUserPlaneCredentialsResponse = Schema.Struct({
  credentials: Schema.Array(AdminUserPlaneCredentialRow),
});
export type AdminUserPlaneCredentialsResponse = typeof AdminUserPlaneCredentialsResponse.Type;
